import { imageAPI } from "@/lib/api";

/**
 * Process an uploaded image file.
 * - JPEG/PNG/WebP: compressed client-side (fast, no server call)
 * - HEIC/other: sent to server for conversion (one at a time)
 */
export async function processImageFile(file) {
  const type = (file.type || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  const isStandard = type === "image/jpeg" || type === "image/png" || type === "image/webp" || type === "image/gif";
  const isHeic = name.endsWith(".heic") || name.endsWith(".heif") || type.includes("heic") || type.includes("heif");

  // Standard formats: compress client-side (fast)
  if (isStandard) {
    return clientSideCompress(file);
  }

  // HEIC or unknown: try client-side first, fall back to server
  if (isHeic || !isStandard) {
    const clientResult = await clientSideCompress(file);
    if (clientResult.preview) {
      return clientResult; // Browser could render it
    }
    // Browser couldn't render — send to server
    return serverConvert(file);
  }

  return clientSideCompress(file);
}

function clientSideCompress(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      const img = new window.Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          const maxDim = 1200;
          let w = img.width;
          let h = img.height;
          if (Math.max(w, h) > maxDim) {
            const ratio = maxDim / Math.max(w, h);
            w = Math.round(w * ratio);
            h = Math.round(h * ratio);
          }
          canvas.width = w;
          canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          const compressed = canvas.toDataURL("image/jpeg", 0.85);
          resolve({ preview: compressed, base64: compressed.split(",")[1] });
        } catch {
          resolve({ preview: dataUrl, base64: dataUrl.split(",")[1] });
        }
      };
      img.onerror = () => {
        // Can't render — return null preview, raw base64
        resolve({ preview: null, base64: dataUrl.split(",")[1] });
      };
      img.src = dataUrl;
    };
    reader.onerror = () => resolve({ preview: null, base64: "" });
    reader.readAsDataURL(file);
  });
}

async function serverConvert(file) {
  const raw64 = await readFileBase64(file);
  try {
    const { data } = await imageAPI.convert(raw64);
    return { preview: data.preview, base64: data.base64 };
  } catch {
    return { preview: null, base64: raw64 };
  }
}

function readFileBase64(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target.result;
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    reader.onerror = () => resolve("");
    reader.readAsDataURL(file);
  });
}
